import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { AutoplayProps } from '..';
import {
  LinkedEpisodeRecording,
  Recording,
  SingleEpisodeRecording,
  SingleRecording,
} from '../../../queries/ats/recordings/types/recording';
import { findRecordingSeries } from '../../../queries/ats/recordings/utils/get-recording-by';
import { routes } from '../../../routes/route-utils';

export function useRecordingAutoplay(recording: Recording, recordings: Recording[]): AutoplayProps | undefined {
  const history = useHistory();
  const { t } = useTranslation();

  const nextEpisode = useNextEpisode(recording, recordings);

  const redirect = useCallback(
    (rec: SingleRecording) => {
      const route = routes.recording(rec, 0);
      return history.replace(route.pathname + route.search);
    },
    [history],
  );

  const seconds = (value: number) => (value === 1 ? t('second') : t('seconds'));

  if (nextEpisode === undefined) {
    return undefined;
  }

  return {
    title: (counter: number) => `${t('play next episode in')} <strong>${counter}</strong> ${seconds(counter)}`,
    subtitle: `${nextEpisode.name} - S${nextEpisode.season.sequenceNumber} E${nextEpisode.episode.sequenceNumber}`,
    image: nextEpisode.image,
    button: {
      label: `${t('watch')} ${t('episode')} ${nextEpisode.episode.sequenceNumber}`,
      onClick: () => redirect(nextEpisode),
    },
    startCountAt: 30,
    onCountEnd: () => redirect(nextEpisode),
  } as AutoplayProps;
}

function useNextEpisode(
  recording: Recording,
  recordings: Recording[],
): SingleEpisodeRecording | LinkedEpisodeRecording | undefined {
  return useMemo(() => {
    if (recording.type !== 'linked') {
      return undefined;
    }

    const series = findRecordingSeries(recording, recordings);

    const nextEpisodeInSeason = series.episodes.find((episode) => {
      return (
        episode.season.sequenceNumber === recording.season.sequenceNumber &&
        episode.episode.sequenceNumber > recording.episode.sequenceNumber
      );
    });

    const nextEpisodeOutOfSeason = series.episodes.find((episode) => {
      return (
        episode.season.sequenceNumber > recording.season.sequenceNumber &&
        episode.episode.sequenceNumber > recording.episode.sequenceNumber
      );
    });

    const episode = nextEpisodeInSeason ?? nextEpisodeOutOfSeason;

    if (!episode) {
      return undefined;
    }

    return episode.status === 'completed' || episode.status === 'ongoing' ? episode : undefined;
  }, [recording, recordings]);
}
